import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "input",
    "item",
    "empty",
    "list",
    "form",
  ];

  static values = {
    clientOnly: { type: Boolean, default: false },
  };

  connect() {
    this.submitTimeout = null;

    this.restoreRenderedValue();
    this.refreshDropdowns();
    if (this.query) this.applyFilter();

    if (this.hasFormTarget) {
      this.formTarget.addEventListener("submit", () => {
        const disabled = [];
        this.formTarget.querySelectorAll("input, select, button").forEach((el) => {
          if (el.disabled || !el.name || el.dataset.keepEmpty !== undefined) return;
          if (el.value === "" || el.name.startsWith("filter_q")) {
            el.disabled = true;
            disabled.push(el);
          }
        });
        setTimeout(() => disabled.forEach((el) => { el.disabled = false; }), 0);
      });
    }
  }

  restoreRenderedValue() {
    if (this.clientOnlyValue) return;

    this.inputTargets.forEach((input) => {
      const rendered = input.parentElement?.dataset.searchValue;
      if (rendered === undefined || input === document.activeElement) return;
      if (input.value !== rendered) input.value = rendered;
    });
  }

  disconnect() {
    if (this.submitTimeout) {
      clearTimeout(this.submitTimeout);
      this.submitTimeout = null;
    }
  }

  itemTargetConnected() {
    if (this.query) this.applyFilter();
  }

  itemTargetDisconnected() {
    if (this.query) this.applyFilter();
  }

  handleFrameLoad() {
    this.refreshDropdowns();
    if (this.query) this.applyFilter();
  }

  get query() {
    return this.hasInputTarget ? this.inputTarget.value.trim().toLowerCase() : "";
  }

  filter() {
    if (!this.hasInputTarget) return;

    this.applyFilter();
    if (!this.clientOnlyValue) this.scheduleSubmit();
  }

  scheduleSubmit(delay = 750) {
    if (!this.hasFormTarget) return;

    if (this.submitTimeout) clearTimeout(this.submitTimeout);

    this.submitTimeout = setTimeout(() => this.submitForm(), delay);
  }

  submitForm() {
    if (!this.hasFormTarget) return;

    if (this.formTarget.requestSubmit) this.formTarget.requestSubmit();
    else this.formTarget.submit();
  }

  refreshDropdowns() {
    if (window.tooltipHandler && typeof window.tooltipHandler.setEventListeners === "function") {
      window.tooltipHandler.setEventListeners();
    }
  }

  applyFilter() {
    const q = this.query;
    const normalizedQuery = this.normalizeText(q);
    const tokens = normalizedQuery ? normalizedQuery.split(" ") : [];
    let visibleCount = 0;

    this.itemTargets.forEach((item) => {
      const haystack = this.normalizeText(item.textContent);
      const isMatch = tokens.length === 0 || tokens.every((token) => haystack.includes(token));

      item.classList.toggle("tw-hidden", !isMatch);

      if (isMatch) {
        this.applyHighlights(item, q);
        visibleCount += 1;
      } else {
        this.clearHighlights(item);
      }
    });

    this.toggleEmptyState(visibleCount, q);
  }

  toggleEmptyState(visibleCount, query) {
    if (!this.hasEmptyTarget) return;
    const showEmpty = query && visibleCount === 0;
    this.emptyTarget.classList.toggle("tw-hidden", !showEmpty);
  }

  applyHighlights(item, query) {
    const tokens = this.normalizeText(query).split(" ").filter(Boolean);
    if (tokens.length === 0) {
      this.clearHighlights(item);
      return;
    }

    const highlightTargets = item.querySelectorAll("[data-highlight-filter]");

    highlightTargets.forEach((element) => {
      if (!element.dataset.highlightFilterOriginalHtml) {
        element.dataset.highlightFilterOriginalHtml = element.innerHTML;
      } else {
        element.innerHTML = element.dataset.highlightFilterOriginalHtml;
      }

      tokens.forEach((token) => this.wrapMatches(element, token));
    });
  }

  clearHighlights(item) {
    const highlightTargets = item.querySelectorAll("[data-highlight-filter]");

    highlightTargets.forEach((element) => {
      if (element.dataset.highlightFilterOriginalHtml) {
        element.innerHTML = element.dataset.highlightFilterOriginalHtml;
      }
    });
  }

  wrapMatches(element, query) {
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(escapedQuery, "gi");
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    const nodes = [];

    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (node.nodeValue && node.nodeValue.trim()) nodes.push(node);
    }

    nodes.forEach((node) => {
      const text = node.nodeValue;
      let match;
      let lastIndex = 0;
      const fragment = document.createDocumentFragment();

      regex.lastIndex = 0;

      while ((match = regex.exec(text)) !== null) {
        const start = match.index;
        const end = start + match[0].length;

        if (start > lastIndex) fragment.appendChild(document.createTextNode(text.slice(lastIndex, start)));

        const highlight = document.createElement("span");
        highlight.className = "search-highlight";
        highlight.textContent = text.slice(start, end);
        fragment.appendChild(highlight);

        lastIndex = end;
      }

      if (lastIndex === 0) return;

      if (lastIndex < text.length) fragment.appendChild(document.createTextNode(text.slice(lastIndex)));

      node.parentNode.replaceChild(fragment, node);
    });
  }

  normalizeText(text) {
    return text.toLowerCase().replace(/[\s\u00a0]+/g, " ").trim();
  }
}
